import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, LoadingController, AlertController, Platform } from '@ionic/angular/lazy';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { OrderService } from 'src/app/services/order.service';
import moment from 'moment-timezone';
import { OrderRefundPaymentAddPage } from 'src/app/modals/order-refund-payment-add/order-refund-payment-add.page';
import { DateTimePickerPage } from 'src/app/modals/date-time-picker/date-time-picker.page';
import { ConnectedAccountService } from 'src/app/services/connected-account.service';
import { StorageService } from 'src/app/services/storage.service';
import { ObserverService } from 'src/app/services/observer.service';
import { Capacitor } from '@capacitor/core';
import { Decimal } from "decimal.js";

@Component({
    selector: 'app-order-refund-add',
    templateUrl: './order-refund-add.page.html',
    styleUrls: ['./order-refund-add.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class OrderRefundAddPage implements OnInit {

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    moment = moment;
    user = null;
    @Input() order_id = null;
    order = null;
    refundable_items = [];
    shipping_refunded = false;
    shipping_quantity = 0;
    xero_connected = false;
    modal_css_class = '';
    formatted_date = null;
    date_modal_css_class = '';
    settings = null;

    params = {
        items: [],
        item_shipping: false,
        add_xero_credit_note: false
    };

    // modal flags
    date_picker_modal_open = false;

    constructor(
        private router: Router,
        private apiService: APIService,
        private authService: AuthService,
        public modalController: ModalController,
        public alertController: AlertController,
        private loadingController: LoadingController,
        private orderService: OrderService,
        private platform: Platform,
        private connectedAccountService: ConnectedAccountService,
        private storageService: StorageService,
        private observerService: ObserverService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
            this.date_modal_css_class = 'backdrop';
        }

        if(this.order_id) {


            this.params['date'] = this.moment().toISOString(true);
            this.formatted_date = this.moment(this.params['date']).format("D MMM YYYY, HH:mm");

            this.loadData().then(() => {

                for(let i = 0; i < this.order.items.length; i++) {
                    if(this.order.items[i].refundable_quantity != 0) {

                        let item = {};
                        item['id'] = this.order.items[i].id;
                        item['title'] = this.order.items[i].title;
                        item['refundable_quantity'] = this.order.items[i].refundable_quantity;
                        item['quantity'] = 0;
                        item['quantity_decimal_places'] = this.order.items[i].quantity_decimal_places;

                        if(this.order.items[i].sku) {
                            item['sku'] = this.order.items[i].sku;
                        }

                        if(this.order.items[i].measurement_unit) {
                            item['measurement_unit'] = this.order.items[i].measurement_unit;
                        }

                        this.refundable_items.push(item);
                    }
                }

                if(this.order.shipping) {
                    if(this.order.refunds) {
                        for(let i = 0; i < this.order.refunds.length; i++) {
                            if(this.order.refunds[i].shipping_refunded == true) {
                                this.shipping_refunded = true;
                                break;
                            }
                        }
                    }
                }

             })
             .catch(() => { });

        }

        this.observerService.subscribe('network_status_changed|order_refund_add_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => { }).catch(() => { });
                }
                else {
                    this.presentAlert("Offline", "You must be online to add a refund, please check your network connection and try again.");
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("network_status_changed|order_refund_add_modal");
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading order").then(() => {
                this.loadOrder().then(() => {
                    this.loading_spinner.message = "Loading settings";
                    this.loadSettings().then(() => {
                        if(this.settings.xero_connected == "true") {
                            this.xero_connected = true;
                        }
                        if(this.xero_connected) {
                            this.params.add_xero_credit_note = true;
                        }
                        this.loading_spinner.dismiss();
                        resolve();
                    })
                    .catch(() => {
                        this.loading_spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    loadSettings() {
        return new Promise<void>((resolve, reject) => {
            this.authService.isLoggedIn().then(() => {
                this.storageService.get("connected_account_id").then(connected_account_id => {
					if (connected_account_id) {
						this.connectedAccountService.getConnectedAccount(connected_account_id).then(connected_account => {
                            if (connected_account) {
                                this.apiService.getSettings(connected_account).then((settings: {}) => {
                                    if(settings) {
                                        this.settings = settings;
                                    }
                                    resolve();
                                })
                                .catch((error) => {
                                    if(error.status === 400) {
                                        this.presentAlert('Unknown error', "Unfortunately something went wrong when trying to retrieve the settings.");
                                    }
                                    else if(error.status == 403) {
                                        this.presentAlert('Insufficient permissions', "You don't have permission to view settings.");
                                    }
                                    else if(error.status == 404) {
                                        this.presentAlert('Unavailable', "Unfortunately we're unable to retrieve settings at this time.");
                                    }
                                    else if(error.status == 0) {
                                        this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                                    }
                                    reject();
                                });
                            } 
                            else {
                                reject();
                            }
                        })
                        .catch(error => {
                            reject(error);
                        });
					} 
                    else {
						reject();
					}
				})
				.catch(error => {
					reject(error);
				});                
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }    

    loadOrder() {
        let promise = new Promise<void>((resolve, reject) => {
            this.order = null;
            this.orderService.getOrder(this.order_id).then((order) => {
                if(order) {
                    this.order = order;
                }
                resolve();
            })
            .catch(() => {
                reject();
            });
        });
        return promise;
    }

    adjustItemQuantity(item_id, change_mode){
        for(let i = 0; i < this.refundable_items.length; i++) {
            if(item_id == this.refundable_items[i].id) {
                let step = Math.pow(10, -this.refundable_items[i].quantity_decimal_places);
                if(change_mode === 'add'){
                    if(this.refundable_items[i].quantity < this.refundable_items[i].refundable_quantity){
                        this.refundable_items[i].quantity = Number(new Decimal(this.refundable_items[i].quantity).plus(step));
                    }
                }
                else if(change_mode === 'subtract'){
                    if(this.refundable_items[i].quantity > 0){
                        this.refundable_items[i].quantity = Number(new Decimal(this.refundable_items[i].quantity).minus(step));
                    }
                }
                break;
            }
        }
    }

    adjustShippingQuantity(change_mode){
        if(change_mode === 'add'){
            if(this.shipping_quantity < 1){
                this.shipping_quantity++;
            }
        }
        else if(change_mode === 'subtract'){
            if(this.shipping_quantity > 0){
                this.shipping_quantity--;
            }
        }
    }

    close() {
        this.modalController.dismiss();
    }

    add() {

        let validated = true;
        let refund_has_items = false;

        for(let i = 0; i < this.refundable_items.length; i++) {

            let item = this.refundable_items[i];
            let quantity_invalid_char = item.quantity.toString().search(/[^0-9.]/g);

            if(quantity_invalid_char !== -1) {
                this.presentAlert('Invalid quantity', `Item "${item.title}" has an invalid quantity.`);
                validated = false;
                break;
            }
            else if(item.quantity > 0) {
                refund_has_items = true;
                if(item.quantity > item.refundable_quantity){
                    this.presentAlert('Over refunded', `You have refunded an item "${item.title}". The max quantity is ${item.refundable_quantity}.`);
                    validated = false;
                    break;
                }
            }
            else if(item.quantity < 0) {
                this.presentAlert('Negative quantity', `Item "${item.title}" has a negative quantity. Please make the quantity greater or equal to zero.`);
                validated = false;
                break;
            }

        }

        if(!refund_has_items && this.shipping_quantity == 0) {
            this.presentAlert('Empty refund', 'You need at least one item.');
            validated = false;
        }

        if(this.params['date'] == null || this.params['date'].trim() === "") {
            validated = false;
            this.presentAlert('Missing date', 'Please select a date.');
        }

        if(validated) {

            let items = [];
            // change lookup into array
            for(let i = 0; i < this.refundable_items.length; i++) {
                if(this.refundable_items[i].quantity > 0) {
                    items.push({
                        id: this.refundable_items[i].id,
                        quantity: this.refundable_items[i].quantity
                    });
                }
            }
            this.params.items = items;

            this.params['date'] = this.moment(this.params['date']).utc().toISOString();

            if(this.shipping_quantity > 0) {
                this.params.item_shipping = true;
            }

            this.addOrderRefund().then((refund: any) => {
                //NOTE: if there's a payment to be made, redirect to add payment
                if(refund.total_price > 0) {
                    this.presentOrderRefundPaymentAddModal(refund.id);
                }
                this.modalController.dismiss();
            });
            
        }
    }

    addOrderRefund() {
        return new Promise((resolve, reject) => {
            this.presentSpinner("Adding refund").then(() => {
                this.orderService.addOrderRefund(this.order.id, this.params).then((refund) =>{
                    if(refund) {
                        this.spinner.dismiss();
                        resolve(refund);
                    }
                })
                .catch(() => {
                    this.spinner.dismiss();
                    reject();
                });
            });
        });
    }

    async presentOrderRefundPaymentAddModal(refund_id) {
        const modal = await this.modalController.create({
            component: OrderRefundPaymentAddPage,
            cssClass: this.modal_css_class,
            componentProps: {
                refund_id: refund_id,
                order_id: this.order_id
            }
        });
        return await modal.present();
    }

    async presentDateTimePickerModal() {
        if(!this.date_picker_modal_open) {
            this.date_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: DateTimePickerPage,
                cssClass: this.date_modal_css_class,
                componentProps: {
                    date: this.params['date'],
                    format: "date-time",
                    clear_date: false
                }
            })
            modal.onDidDismiss().then((data) => {
                this.date_picker_modal_open = false;
                if(data.data) {
                    this.params['date'] = data.data['date'];
                    this.formatted_date = this.moment(this.params['date']).format("D MMM YYYY, HH:mm");
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async editItemQuantity(item) {

        let header = "";

        if(item["measurement_unit"] != undefined && item["measurement_unit"] != null) {
            header = "Quantity" + " " + "(" + item["measurement_unit"].symbol + ")";
        }
        else {
            header = "Quantity";
        }

        const alert = await this.alertController.create({
            header: header,
            cssClass:'alert',
            inputs: [{
                name: 'quantity',
                type: 'number',
                value: item.quantity,
                min: 0
            }],
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                    cssClass: 'secondary',
                    handler: () => {
                    }
                }, 
                {
                    text: 'Ok',
                    handler: (data) => {
                        for(let i = 0; i < this.refundable_items.length; i++) {
                            if(item.id == this.refundable_items[i].id) {

                                let refundable_item = this.refundable_items[i];

                                // if dp=0, only accept numbers (allows negative)
                                if(refundable_item.quantity_decimal_places == 0 && /[^0-9-]/.test(data.quantity)) {
                                    refundable_item.quantity = item.quantity;
                                }
                                // if dp > 0, only accept numbers/decimal points (allows negative)
                                else if(refundable_item.quantity_decimal_places > 0 && /[^0-9.-]/.test(data.quantity)) {
                                    refundable_item.quantity = item.quantity;
                                }
                                else if(refundable_item.quantity_decimal_places > 0 && data.quantity.startsWith('.')) {
                                    refundable_item.quantity = item.quantity;
                                }
                                else if(refundable_item.quantity_decimal_places > 0 && data.quantity.endsWith('.')) {
                                    refundable_item.quantity = item.quantity;
                                }
                                // if dp > 0, ensure decimal places not exceeded
                                else if (refundable_item.quantity_decimal_places > 0 && data.quantity.includes('.') && data.quantity.split('.')[1].length > refundable_item.quantity_decimal_places) {
                                    refundable_item.quantity = item.quantity;
                                }
                                else if(!data.quantity.length) {
                                    refundable_item.quantity = item.quantity;
                                }
                                else if(data.quantity < 0) {
                                    refundable_item.quantity = 0;
                                }
                                else if (data.quantity > refundable_item.refundable_quantity) {
                                    refundable_item.quantity = refundable_item.refundable_quantity;
                                }
                                else {
                                    refundable_item.quantity = Number(new Decimal(data.quantity).toDecimalPlaces(refundable_item.quantity_decimal_places));
                                }

                                break;
                            }
                        }

                    }
                }
            ]
        });
        await alert.present();
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}
